import { Children, Fragment, isValidElement, type ReactElement, type ReactNode } from "react";

import { Divider } from "../../atoms/divider";
import { cn, platformProps } from "../../lib";
import {
  SidebarMenuContext,
  useSidebarMenuContext,
} from "../../molecules/sidebar-nav-item/context";
import { Box } from "../../primitives";
import { sidebarMenuRootClasses } from "./lib";
import { SidebarMenuSection } from "./sidebar-menu-section";
import type { SidebarMenuProps } from "./types";

const isSidebarMenuSection = (child: ReactNode): child is ReactElement<{ divider?: boolean }> => {
  if (!isValidElement(child)) return false;
  const type = child.type as { displayName?: string };
  return type?.displayName === "SidebarMenuSection";
};

const SidebarMenuRoot = ({ collapsed, children, className, testID }: SidebarMenuProps) => {
  // Inherit the flag `AppSidebar` bridges in via `SidebarMenuContext` when no
  // explicit `collapsed` prop is given; standalone (no shell) this defaults to
  // expanded. An explicit prop always wins.
  const ambient = useSidebarMenuContext();
  const isCollapsed = collapsed ?? ambient.collapsed;
  const sections = Children.toArray(children).filter((child) => child != null);
  const lastSectionIndex = (() => {
    for (let i = sections.length - 1; i >= 0; i -= 1) {
      if (isSidebarMenuSection(sections[i])) return i;
    }
    return -1;
  })();

  return (
    <SidebarMenuContext.Provider value={{ collapsed: isCollapsed }}>
      <Box
        as="aside"
        {...platformProps({ testID, slot: "sidebar-menu" })}
        accessibilityLabel="Main navigation"
        className={cn(sidebarMenuRootClasses, className)}
      >
        <Box as="nav" className="flex flex-1 flex-col">
          {sections.map((child, index) => {
            const key = isValidElement(child) ? (child.key ?? index) : index;
            const isSection = isSidebarMenuSection(child);
            const sectionDividerProp = isSection ? child.props.divider : undefined;
            const showDivider =
              isSection && sectionDividerProp !== false && index < lastSectionIndex;

            return (
              <Fragment key={key}>
                {child}
                {showDivider ? <Divider emphasis="medium" /> : null}
              </Fragment>
            );
          })}
        </Box>
      </Box>
    </SidebarMenuContext.Provider>
  );
};

SidebarMenuRoot.displayName = "SidebarMenu";

export { SidebarMenuRoot, SidebarMenuSection };
